/**
 * useDescription hook.
 */
import Event from '@shared/classes/event';
import EpgContext from '@shared/components/utils/context/epgContext';
import MainContext from '@shared/components/utils/context/mainContext';
import MosaicContext from '@shared/components/utils/context/mosaicContext';
import useMetadata from '@shared/components/utils/hooks/useMetadata';
import { ContextType } from '@shared/types/enums/context';
import { DescriptionContextProps } from '@shared/components/utils/context/descriptionContext';
import { selectMainAsset } from '@shared/store/slices/main.slice';
import { setDescription } from '@shared/store/slices/app.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useContext, useEffect, useMemo } from 'react';

export default function useDescription(
  type: ContextType
): DescriptionContextProps {
  // Dispatch
  const dispatch = useAppDispatch();

  // Context
  const epg = useContext(EpgContext);
  const main = useContext(MainContext);
  const mosaic = useContext(MosaicContext);

  // Selectors
  const mainAsset = useAppSelector(selectMainAsset);

  // Retrieve parameters
  const { asset, display } = useMemo(() => {
    switch (type) {
      case ContextType.EPG:
        return {
          asset: epg.asset,
          display: epg.config.display.description,
        };
      case ContextType.MAIN:
        return {
          asset: mainAsset,
          display: main.config.display.description,
        };
      case ContextType.MOSAIC:
        return {
          asset: mosaic.asset,
          display: mosaic.config.description,
        };
    }
    return { asset: undefined, display: false };
  }, [epg, main, mainAsset, mosaic, type]);

  // Effects

  useEffect(() => {
    dispatch(setDescription(display));
  }, [display]);

  useEffect(() => {
    return () => {
      dispatch(setDescription(false));
    };
  }, []);

  // Metadata
  const metadata = useMetadata({ asset: asset as Event });

  // Return values
  return {
    asset: asset as Event | null,
    display,
    metadata,
  };
}
